import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/*
 * The generating light on a shape: Flow's node animation ("ring", the Siri-style edge glow Tarek
 * picked — flobi-flow components/nodes/image/composer/genAnims.jsx), in Artwork's violets
 * (Tarek, 14 Oct: "go pull the one we have in flow please and use the colors of artwork in it").
 *
 * ONE mask (haze + glow + rim, a nine-slice drawn once) over two tiny conic-gradient canvases that
 * turn inside it. Transform / opacity only: nothing repaints per frame and nothing is rastered again when
 * the canvas zooms — Flow's rule for every generating animation.
 */

/** Artwork's accent family, light to deep and back — no transparent stops, so no seams. */
const STOPS: [number, string][] = [
  [0, '#7c5cff'], [45, '#9b7bff'], [90, '#b28aff'], [135, '#d4a6ff'], [180, '#f0c4ff'],
  [215, '#c48bff'], [255, '#8f6bff'], [300, '#6d5bff'], [330, '#a98cff'], [360, '#7c5cff'],
];
const FLOW_PX = 128; // the texture: stretched, and a gradient stays smooth

function Flow({ side, className }: { side: number; className: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useLayoutEffect(() => {
    const g = ref.current?.getContext('2d');
    if (!g) return;
    const c = FLOW_PX / 2;
    const grad = typeof g.createConicGradient === 'function' ? g.createConicGradient(-Math.PI / 2, c, c) : g.createLinearGradient(0, 0, FLOW_PX, FLOW_PX);
    for (const [deg, colour] of STOPS) grad.addColorStop(deg / 360, colour);
    g.fillStyle = grad; g.fillRect(0, 0, FLOW_PX, FLOW_PX);
  }, []);
  return <canvas ref={ref} width={FLOW_PX} height={FLOW_PX} aria-hidden="true" className={className} style={{ width: side, height: side }} />;
}

function framePath(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  g.roundRect(x, y, w, h, Math.max(0, Math.min(r, w / 2, h / 2)));
}

/** Alpha 1 at the edges → 0 inside, at quarter resolution (the blur runs once, here). */
function edgeBand(w: number, h: number, depth: number, radius: number) {
  const k = 0.25, cw = Math.max(8, Math.round(w * k)), ch = Math.max(8, Math.round(h * k));
  const c = document.createElement('canvas'); c.width = cw; c.height = ch;
  const g = c.getContext('2d')!;
  g.fillStyle = '#fff'; g.fillRect(0, 0, cw, ch);
  g.globalCompositeOperation = 'destination-out';
  g.filter = `blur(${Math.max(1, depth * k * 0.45)}px)`;
  const inset = depth * k * 0.55;
  g.beginPath(); framePath(g, inset, inset, cw - inset * 2, ch - inset * 2, radius * k); g.fill();
  return c;
}

const HAZE_A = 0.2, GLOW_A = 0.72, RIM_A = 0.95, RIM_W = 1.5, HAZE_DEPTH = 44, GLOW_DEPTH = 18;
/** How far in the haze has any light left (its blur has died by here), and the tile that holds two of them. */
const SLICE = 80, TILE = SLICE * 2 + 8, TILE_DPR = 2;
const masks = new Map<number, Promise<string | null>>();
/**
 * One tile per corner radius: haze + glow + rim, sliced in nine so its corners stay put and its edges
 * stretch. Never one per size: a new mask-image is loaded late and the light is gone until it lands,
 * every 8px of a zoom (Tarek, 2 Nov: "zooming in and out is making the glow effect flicker").
 */
function glowMask(radius: number) {
  const held = masks.get(radius);
  if (held) return held;
  const W = TILE * TILE_DPR;
  const out = document.createElement('canvas'); out.width = W; out.height = W;
  const g = out.getContext('2d')!;
  g.imageSmoothingQuality = 'high'; g.globalCompositeOperation = 'lighter';
  g.globalAlpha = HAZE_A; g.drawImage(edgeBand(TILE, TILE, HAZE_DEPTH, radius), 0, 0, W, W);
  g.globalAlpha = GLOW_A; g.drawImage(edgeBand(TILE, TILE, GLOW_DEPTH, radius), 0, 0, W, W);
  g.globalAlpha = RIM_A; g.strokeStyle = '#fff'; g.lineWidth = RIM_W * TILE_DPR;
  const half = (RIM_W * TILE_DPR) / 2;
  g.beginPath(); framePath(g, half, half, W - half * 2, W - half * 2, Math.max(0, (radius - RIM_W / 2) * TILE_DPR)); g.stroke();
  const made = new Promise<string | null>(resolve => out.toBlob(blob => resolve(blob ? URL.createObjectURL(blob) : null), 'image/png'));
  masks.set(radius, made);
  return made;
}

/** The nine-slice as a style. Firefox has no mask-box-image, so the tile is stretched over the box there. */
function maskStyle(url: string, w: number, h: number) {
  if (!CSS.supports('-webkit-mask-box-image', 'none')) return { maskImage: `url(${url})`, maskSize: '100% 100%' };
  // A small box should not fill with light; a big one keeps a thin band.
  const scale = Math.max(0.5, Math.min(1, Math.min(w, h) / 440));
  return { WebkitMaskBoxImage: `url(${url}) ${SLICE * TILE_DPR} / ${SLICE * scale}px stretch` };
}

/** The box's layout size. */
function useBoxSize(ref: RefObject<HTMLElement | null>) {
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setSize({ w: el.offsetWidth, h: el.offsetHeight });
    const observer = new ResizeObserver(([entry]) => entry && setSize({ w: Math.round(entry.contentRect.width), h: Math.round(entry.contentRect.height) }));
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return size;
}

/** The light round a box's edge. Fills its (positioned) parent. */
export function GenerationGlow({ radius = 0 }: { radius?: number }) {
  const ref = useRef<HTMLDivElement>(null), size = useBoxSize(ref);
  const [mask, setMask] = useState<string | null>(null);
  const w = size?.w || 0, h = size?.h || 0;
  useEffect(() => {
    let live = true;
    void glowMask(radius).then(url => { if (live && url) setMask(url); });
    return () => { live = false; };
  }, [radius]);
  // Covers the box at every angle while it turns.
  const side = Math.ceil(Math.hypot(w, h)) + 2;
  return <div ref={ref} className="artwork-ai-glow" aria-hidden="true"
    style={mask ? maskStyle(mask, w, h) : { visibility: 'hidden' }}>
    {w > 0 && <><Flow side={side} className="artwork-ai-glow-flow" /><Flow side={side} className="artwork-ai-glow-flow is-reverse" /></>}
  </div>;
}
